import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import { keyframes } from "@mui/system";

// Animación sutil para SEV
const pulse = keyframes`
  0%   { transform: scale(1);    opacity: 1;    box-shadow: 0 0 0 rgba(255,255,255,0); }
  50%  { transform: scale(1.04); opacity: 1;    box-shadow: 0 0 15px rgba(229,50,45,0.4); }
  100% { transform: scale(1);    opacity: 1;    box-shadow: 0 0 0 rgba(255,255,255,0); }
`;

const LEVELS = [
  { label: "Light", code: "LGT", start: "#ffff00", end: "#ffff00" },
  { label: "Moderate", code: "MOD", start: "#ff8c00", end: "#ff8c00" },
  { label: "Severe", code: "SEV", start: "#e5322d", end: "#e5322d" },
];

const containerSx = (hideBelowWidth = 0) => ({
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  mt: -1,
  opacity: 1,
  transition: "opacity .25s ease",
  [`@media (max-width:${hideBelowWidth}px)`]: { opacity: 0 },
});

function overlayFor(levelCode, variant) {
  const code = `${levelCode}`.toUpperCase();
  if (variant === "pattern") {
    if (code.includes("LGT")) return { background: `radial-gradient(rgba(0,0,0,.12) 1px, transparent 1px)`, backgroundSize: "5px 5px", opacity: 0.4 };
    if (code.includes("MOD")) return { background: `repeating-linear-gradient(45deg, rgba(0,0,0,.1) 0 6px, transparent 6px 12px)`, opacity: 0.4 };
    return { background: `repeating-linear-gradient(45deg, rgba(255,255,255,.2) 0 6px, transparent 6px 12px)`, opacity: 0.5 };
  }
  return undefined;
}

function textColorFor(hex) {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? "#000000" : "#FFFFFF"; // Contraste máximo
}

export default function RiskSelectorCNN_VERTICAL({
  levels = LEVELS,
  hideBelowWidth = 0,
  interactive = false,
  onSelect,
  size = "mini",
  symbolVariant = "pattern",
  equalWidth = true,
}) {
  const isMini = size === "mini";

  return (
    <Box sx={{
      position: "fixed",
      bottom: { xs: 265, md: 220 },
      left: { xs: 65, md: 55 },
      transform: "rotate(-90deg)",
      transformOrigin: "bottom left",
      zIndex: 1000,
      zoom: { xs: 0.8, md: 1 }
    }}>
      <Box component="span" sx={{
        display: 'block',
        width: '100%',
        textAlign: 'center',
        mb: 2.0, // Mayor separación como pediste
        typography: 'caption',
        fontWeight: 900,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: '#FFFFFF', // Blanco sólido
        opacity: 1,      // Sin opacidad para que se vea intenso
        textShadow: '0 2px 4px rgba(0,0,0,0.5)', // Sombra para resaltar sobre el mapa
        lineHeight: 1,
      }}>
        <ThunderstormIcon sx={{ fontSize: 13, color: 'inherit', marginRight: 0.7, verticalAlign: 'middle' }} aria-hidden />
        Convective Risk
      </Box>

      <Box sx={containerSx(hideBelowWidth)}>
        <Box sx={{
          display: "inline-flex",
          borderRadius: 999,
          overflow: "hidden",
          pointerEvents: interactive ? "auto" : "none",
          boxShadow: "0 8px 25px rgba(0,0,0,0.4)" // Sombra exterior más fuerte
        }}>
          {levels.map((lvl, i) => {
            const code = (lvl.code ?? lvl.label).toString().toUpperCase();
            const after = overlayFor(code, symbolVariant);
            const isSevere = code.includes("SEV");

            return (
              <Tooltip key={lvl.code} title={`${lvl.label}`} arrow placement="top" disableInteractive>
                <Button
                  disableElevation
                  disableRipple
                  onClick={interactive && onSelect ? () => onSelect(lvl.code) : undefined}
                  sx={{
                    position: "relative",
                    minWidth: equalWidth ? (isMini ? 75 : 85) : 0,
                    px: isMini ? 1.2 : 1.5,
                    py: isMini ? 0.6 : 0.8,
                    fontSize: isMini ? ".65rem" : ".75rem",
                    fontWeight: 800,
                    lineHeight: 1,
                    textTransform: "none",
                    color: textColorFor(lvl.start),
                    backgroundColor: lvl.start, // COLOR INTENSO SÓLIDO
                    borderRadius: 0,
                    borderRight: i < levels.length - 1 ? "1px solid rgba(0,0,0,0.1)" : 0,
                    animation: isSevere ? `${pulse} 2s infinite ease-in-out` : "none",
                    transition: "transform .2s ease, filter .2s ease",

                    "&:hover": {
                      filter: "brightness(1.1)",
                      zIndex: 2,
                    },

                    '&::after': after && after.background ? {
                      content: '""', position: 'absolute', inset: 0,
                      background: after.background, opacity: after.opacity,
                      mixBlendMode: 'multiply', pointerEvents: 'none'
                    } : undefined,
                  }}
                >
                  {lvl.label}
                </Button>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}